import React from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, useWindowDimensions } from 'react-native';

import { MIN_SEARCH_CHARS } from '@src/constants/constants';

import { Box, Text } from '../Restyle';
import Icon from '../shared/Icon/Icon';

export interface EmptyStateNoSearchProps {
  showSmall?: boolean;
  // 'typeMore' is shown while the term is below MIN_SEARCH_CHARS, before any
  // search has fired — distinct from 'noResults', which only applies once a
  // search has settled and returned nothing (TA-10091).
  // 'noFilterResults' is for a filter (not a text search) matching nothing.
  // 'noTasksFound' is the task-search "no results" state (TA-10495 #18): a
  // Hash icon, a caller-supplied `title`, and no description.
  variant?: 'noResults' | 'typeMore' | 'noFilterResults' | 'noTasksFound';
  /** Overrides the variant's default title (TA-10495 #18 — task search
   * supplies its own "No current/completed tasks found" copy). */
  title?: string;
  /** Overrides the variant's default description. */
  description?: string;
  /** Hides the description line below the title. Defaults to shown; the
   * 'noTasksFound' variant (TA-10495 #18) doesn't have one at all. */
  showDescription?: boolean;
}
const EmptyStateNoSearch: React.FC<EmptyStateNoSearchProps> = ({
  showSmall = false,
  variant = 'noResults',
  title: titleOverride,
  description: descriptionOverride,
  showDescription = true,
}) => {
  const { height, width } = useWindowDimensions();
  const { t } = useTranslation('models');
  const EMPTY_ICON_WIDTH = width > 375 ? width / 2 : width / 1.8;
  const EMPTY_ICON_HEIGHT = height > 750 ? height / 6.2 : height / 5;
  let title = t('chat.emptyState.noResults');
  let message = t('chat.emptyState.noSearchMessage');
  if (variant === 'typeMore') {
    title = t('chat.emptyState.typeMoreCharactersTitle');
    message = t('chat.emptyState.typeMoreCharactersMessage', {
      count: MIN_SEARCH_CHARS,
    });
  } else if (variant === 'noFilterResults') {
    title = t('chat.emptyState.noFilterResultsTitle');
    message = t('chat.emptyState.noFilterResultsMessage');
  } else if (variant === 'noTasksFound') {
    message = '';
  }
  if (titleOverride !== undefined) {
    title = titleOverride;
  }
  if (descriptionOverride !== undefined) {
    message = descriptionOverride;
  }
  const shouldShowDescription = showDescription && variant !== 'noTasksFound';
  // The compact layout is shared by native and web for the task-search
  // 'noTasksFound' variant (Copilot review): the web branch below always
  // renders the large NoSearchResults illustration, so without this the
  // variant's Hash icon could never appear on web. Every other variant keeps
  // its existing per-platform layout untouched.
  if (showSmall && (Platform.OS !== 'web' || variant === 'noTasksFound')) {
    const smallIconNameForVariant: Record<
      NonNullable<EmptyStateNoSearchProps['variant']>,
      'Hash' | 'SlidersThin' | 'Search'
    > = {
      noTasksFound: 'Hash',
      noFilterResults: 'SlidersThin',
      noResults: 'Search',
      typeMore: 'Search',
    };
    const smallIconName = smallIconNameForVariant[variant];
    return (
      <Box
        flex={1}
        padding='m'
        justifyContent='center'
        alignItems='center'
        testID='empty-state-no-search'>
        <Box
          width={64}
          height={64}
          borderRadius='xl'
          backgroundColor='grey01'
          alignItems='center'
          justifyContent='center'
          marginBottom='s'>
          <Icon name={smallIconName} variant='xxl' color='textPrimary' />
        </Box>
        <Text
          variant='labelEmphasizedChatDetialTitle'
          textAlign='center'
          marginBottom='xxs'>
          {title}
        </Text>
        {shouldShowDescription && (
          <Text variant='bodySecondaryNew' textAlign='center' color='grey04'>
            {message}
          </Text>
        )}
      </Box>
    );
  }

  if (Platform.OS === 'web') {
    return (
      <Box
        flex={1}
        padding={showSmall ? 'none' : 'm'}
        justifyContent='center'
        testID='empty-state-no-search'
        marginTop='listFooter'
        marginHorizontal={showSmall ? 'none' : 'listFooter'}>
        <Box
          flex={1}
          justifyContent='center'
          alignItems='center'
          flexDirection='column'
          marginVertical='m'>
          <Icon name='NoSearchResults' width={200} height={151} />
          <Text
            variant={showSmall ? 'heading2' : 'heading1'}
            textAlign='center'
            paddingBottom='xs'
            color='onSurfaceSecondary'>
            {title}
          </Text>
          {shouldShowDescription && (
            <Text variant='body' textAlign='center' color='onSurfaceSecondary'>
              {message}
            </Text>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box flex={1} padding='xs' justifyContent='center' marginTop='xxl'>
      <Box alignItems='center' marginTop='l'>
        <Icon
          name='NoSearchResults'
          width={EMPTY_ICON_WIDTH}
          height={EMPTY_ICON_HEIGHT}></Icon>
      </Box>

      <Box flexDirection='column'>
        <Text
          variant='heading1'
          textAlign='center'
          paddingBottom='xs'
          color='onSurfaceSecondary'>
          {title}
        </Text>
        {shouldShowDescription && (
          <Text
            variant='body'
            textAlign='center'
            color='onSurfaceSecondary'
            marginHorizontal='m'>
            {message}
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default EmptyStateNoSearch;
